import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { ActionsRegistry } from './ActionsRegistry';

interface Context {
  actionsRegistry: ActionsRegistry;
  shortcutsDisabled: boolean;
}

/** @internal Exported for useKeyActions only */
export const KeyActionContext = React.createContext<Partial<Context>>({});
KeyActionContext.displayName = 'KeyActionContext';

/**
 * In order to use `<KeyActions>` or `useKeyActions()`, need a
 * `KeyActionsProvider` mounted somewhere in the hierarchy.  The
 * `KeyActionsProvider` creates a `ActionsRegistry` instance which keeps track
 * of all registered key actions so we can show them in the `ShortcutHelp()`
 * component, and which is also responsible for actually listening for keyboard
 * events.
 *
 * When `shortcutsDisabled` is true, no shortcuts are registered (e.g. restricted user modes).
 */
export const KeyActionsProvider: React.FC<{
  children: React.ReactNode;
  shortcutsDisabled?: boolean;
}> = (props) => {
  const contextValueRef = useRef({
    actionsRegistry: new ActionsRegistry(),
  });

  const { actionsRegistry } = contextValueRef.current;
  const shortcutsDisabled = props.shortcutsDisabled ?? false;

  const contextValue = useMemo(
    () => ({
      actionsRegistry,
      shortcutsDisabled,
    }),
    [actionsRegistry, shortcutsDisabled],
  );

  useEffect(() => {
    return () => {
      // If the KeyActionsProvider ever unmounts, remove all the keys it has
      // registered.
      actionsRegistry.reset();
    };
  }, [actionsRegistry]);

  return (
    <KeyActionContext.Provider value={contextValue}>{props.children}</KeyActionContext.Provider>
  );
};

function useKeyActionContext(): Context {
  const result = useContext(KeyActionContext);
  if (!result.actionsRegistry) {
    throw new Error(`Missing KeyActionProvider.`);
  }
  return {
    actionsRegistry: result.actionsRegistry,
    shortcutsDisabled: result.shortcutsDisabled ?? false,
  };
}

/**
 * Hook which gives components access to the registry.
 */
export function useKeyActionsRegistry() {
  return useKeyActionContext().actionsRegistry;
}
